import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldCheck, ShieldAlert, Clock, Link2, AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useContractLedger, type SealStatus } from '../hooks/useContractLedger';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Props {
  contratoId: string;
}

const SEAL_CONFIG: Record<SealStatus, {
  icon: React.ElementType;
  color: string;
  bg: string;
  border: string;
  label: string;
  tooltip: string;
}> = {
  integro: {
    icon: ShieldCheck,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    border: 'border-emerald-200 dark:border-emerald-800',
    label: 'Íntegro',
    tooltip: 'Este período foi verificado criptograficamente. Os dados não foram alterados desde a aprovação do boletim.',
  },
  pendente: {
    icon: Clock,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-800',
    label: 'Pendente',
    tooltip: 'Verificação criptográfica pendente para este período.',
  },
  comprometido: {
    icon: ShieldAlert,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-800',
    label: 'Comprometido',
    tooltip: 'Inconsistência detectada na cadeia criptográfica deste período.',
  },
};

function detectGaps(periods: { periodo_competencia: string }[]): Set<number> {
  const gapIndices = new Set<number>();
  for (let i = 1; i < periods.length; i++) {
    const prev = new Date(periods[i - 1].periodo_competencia);
    const curr = new Date(periods[i].periodo_competencia);
    const diffMonths = (curr.getFullYear() - prev.getFullYear()) * 12 + (curr.getMonth() - prev.getMonth());
    if (diffMonths > 2) gapIndices.add(i);
  }
  return gapIndices;
}

function overallMessage(seal: SealStatus, totalPeriods: number, validPeriods: number, hasGaps: boolean, hasSuperseded: boolean): string {
  if (totalPeriods === 0) return 'Aguardando registro criptográfico de medições';
  if (seal === 'comprometido') return 'Inconsistência detectada na cadeia — ação corretiva necessária';
  if (seal === 'integro' && !hasGaps && !hasSuperseded) return 'Histórico íntegro e contínuo';
  if (seal === 'integro' && hasGaps) return 'Cadeia íntegra porém com intervalos entre períodos';
  if (seal === 'integro' && hasSuperseded) return 'Cadeia íntegra — contém reconstruções anteriores';
  if (validPeriods === 0) return 'Períodos não verificados — selo pendente';
  return `${validPeriods} de ${totalPeriods} períodos verificados`;
}

function PeriodBadge({ status, periodo, numero, isGap }: { status: SealStatus; periodo: string; numero: number | null; isGap: boolean }) {
  const cfg = SEAL_CONFIG[status];
  const Icon = cfg.icon;
  const dateStr = periodo ? format(parseISO(periodo), 'MMM/yy', { locale: ptBR }) : '—';

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-col items-center gap-0.5">
            {isGap && (
              <div className="flex items-center gap-0.5 text-amber-500 dark:text-amber-400 mb-0.5">
                <AlertTriangle className="h-2.5 w-2.5" />
                <span className="text-[9px] font-medium">gap</span>
              </div>
            )}
            <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium ${cfg.bg} ${cfg.border} ${cfg.color} cursor-default ${isGap ? 'ring-1 ring-amber-400/50' : ''}`}>
              <Icon className="h-2.5 w-2.5 shrink-0" />
              <span className="capitalize">{dateStr}</span>
              {numero != null && <span className="text-[9px] opacity-60">#{numero}</span>}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-xs">
          <p>{cfg.tooltip}</p>
          {isGap && <p className="mt-1 text-amber-600 dark:text-amber-400">⚠ Intervalo temporal detectado antes deste período.</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export const IFIntegritySeal: React.FC<Props> = ({ contratoId }) => {
  const ledger = useContractLedger(contratoId);

  if (ledger.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-4 w-36" />
        <div className="flex gap-2">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-6 w-16" />)}
        </div>
      </div>
    );
  }

  if (ledger.totalPeriods === 0) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Link2 className="h-3.5 w-3.5 text-muted-foreground/60" />
          <p className="text-[11px] font-semibold text-muted-foreground tracking-widest uppercase">
            Selo de Integridade
          </p>
        </div>
        <p className="text-xs text-muted-foreground/60 italic">
          Aguardando registro criptográfico de medições
        </p>
      </div>
    );
  }

  const gapIndices = detectGaps(ledger.periods);
  const hasGaps = gapIndices.size > 0;
  const hasSuperseded = false;

  const overallCfg = SEAL_CONFIG[ledger.overallSeal];
  const OverallIcon = overallCfg.icon;
  const message = overallMessage(ledger.overallSeal, ledger.totalPeriods, ledger.validPeriods, hasGaps, hasSuperseded);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Link2 className="h-3.5 w-3.5 text-muted-foreground/60" />
          <p className="text-[11px] font-semibold text-muted-foreground tracking-widest uppercase">
            Selo de Integridade
          </p>
        </div>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${overallCfg.bg} ${overallCfg.color}`}>
                <OverallIcon className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold">{overallCfg.label}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs text-xs">
              <p>{ledger.validPeriods} de {ledger.totalPeriods} períodos verificados criptograficamente</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Interpretive message */}
      <p className={`text-xs mb-3 ${ledger.overallSeal === 'comprometido' ? 'text-red-600 dark:text-red-400 font-medium' : 'text-muted-foreground/80'}`}>
        {message}
      </p>

      {/* Summary numbers */}
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground mb-3">
        <span className="text-emerald-600 dark:text-emerald-400 font-medium">{ledger.validPeriods} íntegros</span>
        {ledger.pendingPeriods > 0 && (
          <span className="text-amber-600 dark:text-amber-400">{ledger.pendingPeriods} pendentes</span>
        )}
        {ledger.compromisedPeriods > 0 && (
          <span className="text-red-600 dark:text-red-400 font-bold">{ledger.compromisedPeriods} comprometidos</span>
        )}
        {hasGaps && (
          <span className="text-amber-500 dark:text-amber-400">{gapIndices.size} gap(s)</span>
        )}
      </div>

      {/* Period timeline */}
      <div className="flex flex-wrap gap-1">
        {ledger.periods.map((p, idx) => (
          <PeriodBadge
            key={p.sequencia}
            status={p.sealStatus}
            periodo={p.periodo_competencia}
            numero={p.numero_boletim}
            isGap={gapIndices.has(idx)}
          />
        ))}
      </div>
    </div>
  );
};
